import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { getOrders } from '../api/ordersApi.js';
import { getMyInquiries, confirmInquiry } from '../api/inquiriesApi.js';
import { getSavedCards, createSavedCard, updateSavedCard, deleteSavedCard, setDefaultCard } from '../api/savedCardsApi.js';
import { updateProfile, changePassword, getAddresses, createAddress, updateAddress, setDefaultAddress, deleteAddress, getPoints } from '../api/accountApi.js';
import { fmtDateTaipei, fmtTime } from '../utils/format.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import Icon from '../components/ui/Icon.jsx';
import {
  MENU_ITEMS,
  MENU_ACTIONS,
  NOTIFS,
  PAY_METHODS,
} from '../data/account.js';
import './AccountPage.css';

// 登記（詢問單）進度條：4 步。已收錢（PAID）是後台記帳用狀態，進度條歸到「處理中」位置，
// 精確狀態由上方 chip 顯示「已收錢（未結案）」。
const INQUIRY_STEPS = ['已送出', '處理中', '已完成', '已結案'];
const INQUIRY_STEP = { SUBMITTED: 1, PROCESSING: 2, PAID: 2, DONE: 3, CLOSED: 4 };
// 付款代碼（transfer/linepay/paypal）→ 中文；查不到就顯示原值
const payLabel = (code) => PAY_METHODS.find(p => p.id === code)?.label || code || '—';

const VALID_TABS = MENU_ITEMS
  .filter(m => m.action && m.action !== MENU_ACTIONS.LOGOUT)
  .map(m => m.action);

function AccountPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTab = searchParams.get('tab');
  const initialTab = VALID_TABS.includes(queryTab) ? queryTab : MENU_ACTIONS.DASHBOARD;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [sideOpen, setSideOpen] = useState(false);
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // 複製推薦碼到剪貼簿
  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(user.referralCode);
      showToast('推薦碼已複製', 'success', 2000);
    } catch {
      showToast('複製失敗，請手動複製', 'error', 2000);
    }
  };
  const { data: orders = [], loading: ordersLoading } = useApi(() => getOrders(), []);
  const { data: inquiries = [], loading: inquiriesLoading, error: inquiriesError, refetch: refetchInquiries }
    = useApi(() => getMyInquiries(), []);
  // 紅利點數（餘額 + 到期日 + 明細）
  const { data: pointsData } = useApi(() => getPoints(), []);

  // 從站內其他頁（如登記成功）帶 ?tab= 進來時同步切換
  useEffect(() => {
    if (queryTab && VALID_TABS.includes(queryTab) && queryTab !== activeTab) {
      setActiveTab(queryTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTab]);

  if (!user) {
    return <div className="acct-page"><div style={{ padding: 80, textAlign: 'center' }}>載入中…</div></div>;
  }

  const handleMenuClick = async (item) => {
    if (item.action === MENU_ACTIONS.LOGOUT) {
      await logout();
      showToast('已登出', 'info', 2500);
      navigate('/');
      return;
    }
    setActiveTab(item.action);
    setSearchParams(item.action === MENU_ACTIONS.DASHBOARD ? {} : { tab: item.action }, { replace: true });
    setSideOpen(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const goTab = (action) => {
    setActiveTab(action);
    setSearchParams(action === MENU_ACTIONS.DASHBOARD ? {} : { tab: action }, { replace: true });
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const safeName = user.name || user.email || '會員';
  const points = pointsData?.balance ?? 0;
  // 累積消費 / 訂單數：實體訂單（排除已取消）＋ 虛擬商品代打登記
  const activeOrders = orders.filter(o => o.status !== 'cancelled');
  const totalSpent = activeOrders.reduce((s, o) => s + (o.total || 0), 0)
    + inquiries.reduce((s, i) => s + Math.max(0, (i.estimatedTotal || 0) - (i.pointsRedeemed || 0)), 0);
  const orderCount = activeOrders.length + inquiries.length;
  const initial = user.initial || safeName.slice(0, 1);

  const ctx = {
    user, safeName, points, totalSpent, orderCount,
    orders, ordersLoading,
    inquiries, inquiriesLoading, inquiriesError, refetchInquiries,
    goTab, navigate,
  };

  const screens = {
    [MENU_ACTIONS.DASHBOARD]: <DashboardScreen {...ctx} />,
    [MENU_ACTIONS.ORDERS]:    <OrdersScreen {...ctx} />,
    [MENU_ACTIONS.BOOST]:     <BoostScreen {...ctx} />,
    [MENU_ACTIONS.POINTS]:    <PointsScreen points={points} data={pointsData} />,
    [MENU_ACTIONS.ADDRESSES]: <AddressesScreen />,
    [MENU_ACTIONS.PROFILE]:   <ProfileScreen user={user} safeName={safeName} initial={initial} />,
    [MENU_ACTIONS.NOTIFS]:    <NotifsScreen />,
  };

  return (
    <div className="acct-page">
      {/* mobile-only sidebar toggle */}
      <button
        type="button"
        className="acct-side-toggle"
        aria-expanded={sideOpen}
        onClick={() => setSideOpen(o => !o)}
      >
        <span className="acct-avatar" style={{ width: 36, height: 36, fontSize: 14 }}>{initial}</span>
        <span className="acct-side-toggle-text">
          {safeName}
          <small>{user.email}</small>
        </span>
        <span className="acct-side-toggle-chev"><Icon name="chevron" size={14} /></span>
      </button>

      {/* sidebar */}
      <aside className={sideOpen ? 'acct-side open' : 'acct-side'}>
        <div className="acct-profile">
          <div className="acct-avatar">{initial}</div>
          <div className="acct-profile-info">
            <b className="acct-profile-name">{safeName}</b>
            <small className="acct-profile-email">{user.email}</small>
            {user.referralCode && (
              <small className="acct-profile-email" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontWeight: 700, color: 'var(--navy)' }}>
                推薦碼 {user.referralCode}
                <button
                  type="button"
                  onClick={copyReferral}
                  aria-label="複製推薦碼"
                  title="複製推薦碼"
                  style={{ display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--navy)' }}
                >
                  <Icon name="copy" size={14} />
                </button>
              </small>
            )}
          </div>
        </div>
        <nav className="acct-menu">
          {MENU_ITEMS.map((item, i) => (
            item.divider
              ? <div className="hr" key={`hr-${i}`} />
              : <a
                  key={item.label}
                  className={activeTab === item.action ? 'on' : ''}
                  onClick={() => handleMenuClick(item)}
                  style={item.danger ? { color: 'var(--red)' } : undefined}
                >
                  {item.label}
                  {item.badge && <span className="badge">{item.badge}</span>}
                  {item.isNew && <span className="new-dot" />}
                </a>
          ))}
        </nav>
      </aside>

      <section className="acct-main">{screens[activeTab]}</section>
    </div>
  );
}

/* ---------------- 共用 ---------------- */
// API 的 items 是物件陣列；mock 舊資料是字串 + picCount，兩種都要能顯示
const orderSummary = (o) => Array.isArray(o.items)
  ? o.items.map(i => (i.qty > 1 ? `${i.name} ×${i.qty}` : i.name)).join('、')
  : (o.items || `${o.picCount ?? 1} 件商品`);

function OrdersTable({ orders }) {
  return (
    <table className="otable">
      <thead><tr><th>訂單</th><th>內容</th><th>金額</th><th>狀態</th><th /></tr></thead>
      <tbody>
        {orders.map(o => (
          <tr key={o.id}>
            <td><span className="oid">{o.id}<small>{fmtTime(o.createdAt)}</small></span></td>
            <td>{orderSummary(o)}</td>
            <td><span className="oamt">NT${(o.total ?? 0).toLocaleString()}</span></td>
            <td><span className={`ostatus ${o.status}`}>{o.statusLabel}</span></td>
            <td>
              <div className="oaction-wrap">
                <Link className="olink" to={`/account/orders/${o.id}`}>查看</Link>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RegStatusChip({ status, statusLabel }) {
  // CSS class 用小寫狀態（.reg-chip.processing 等）；文字用後端給的 statusLabel
  return <span className={`reg-chip ${String(status || '').toLowerCase()}`}>{statusLabel || status}</span>;
}

// r = InquiryDto：{ id, refNo, productName, payMethod, status, statusLabel, estimatedTotal, pointsRedeemed,
//                   createdAt, items:[{ id, variantName, cardName, account, note, amount }] }
function RegRecord({ r, onConfirm, confirming }) {
  const items = Array.isArray(r.items) ? r.items : [];
  const step = INQUIRY_STEP[r.status] ?? 1; // 非預期狀態停在第 1 步，不崩
  const [open, setOpen] = useState(r.status === 'PROCESSING' || r.status === 'DONE');
  // estimatedTotal 是折抵前總額；折抵後應付 = 總額 − 已折抵點數（1 點 = NT$1）
  const redeemed = r.pointsRedeemed || 0;
  const payable = Math.max(0, (r.estimatedTotal ?? 0) - redeemed);
  return (
    <div className={`rrec ${open ? 'open' : ''}`}>
      <div className="rrec-head" onClick={() => setOpen(o => !o)}>
        {/* 顯示業務編號 refNo（隨機尾碼不洩漏總筆數），不用流水號 id */}
        <span className="rrec-id">{r.refNo || '登記紀錄'}<small>{fmtTime(r.createdAt)}</small></span>
        <span className="rrec-svc">{r.productName}　·　{items.length} 張卡</span>
        <div className="rrec-meta">
          <RegStatusChip status={r.status} statusLabel={r.statusLabel} />
          <span className="rrec-amt">
            {redeemed > 0 && <s className="rrec-amt-orig">NT${(r.estimatedTotal ?? 0).toLocaleString()}</s>}
            NT${payable.toLocaleString()}
          </span>
          <span className="rrec-chev"><Icon name="chevron" size={18} /></span>
        </div>
      </div>
      <div className="rrec-body">
        <div className="rprog">
          {INQUIRY_STEPS.map((t, i) => (
            <div key={t} className={`rstep ${i < step - 1 ? 'on' : ''} ${i === step - 1 ? 'cur' : ''}`}>
              <div className="d">{i < step - 1 ? <Icon name="check" size={12} /> : i + 1}</div>
              <small>{t}</small>
            </div>
          ))}
        </div>
        <div className="rrec-cards">
          {items.map((c, i) => (
            <div className="rrec-cardrow" key={c.id ?? i}>
              <Icon name="user" size={16} style={{ color: 'var(--steel)' }} />
              <span className="nm">{c.cardName || '—'}</span>
              <span className="ac">{c.account || ''}</span>
              <span className="nt">{c.note || '—'}</span>
              <span className="gl">{c.variantName || '—'}</span>
              <span className="am">NT${(c.amount ?? 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
        {redeemed > 0 && (
          <div className="rrec-sum">
            <div><span>預估總額</span><b>NT${(r.estimatedTotal ?? 0).toLocaleString()}</b></div>
            <div><span>紅利折抵</span><b className="minus">−NT${redeemed.toLocaleString()} P</b></div>
            <div className="net"><span>折抵後應付</span><b>NT${payable.toLocaleString()}</b></div>
          </div>
        )}
        <div className="rrec-pay">付款方式：{payLabel(r.payMethod)}</div>
        {/* 只有「已完成」才出現確認結案；按下後狀態轉「已結案」並收進已結案區 */}
        {r.status === 'DONE' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={confirming}
              onClick={(e) => { e.stopPropagation(); onConfirm(r); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 999, border: 'none', background: 'var(--navy)', color: '#fff', font: '800 13px "Noto Sans TC", sans-serif', cursor: confirming ? 'wait' : 'pointer' }}
            >
              <Icon name="check" size={15} /> {confirming ? '處理中…' : '確認完成 · 結案'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- 總覽 ---------------- */
function DashboardScreen({ safeName, points, totalSpent, orderCount, orders, ordersLoading, inquiries, goTab, navigate }) {
  const latest = inquiries[0]; // 可能 undefined（還沒登記過）
  return (
    <>
      <div className="acc-hero">
        <h2>嗨，{safeName}。歡迎回來。</h2>
        <p>這裡可以查看你的訂單、代打登記與紅利點數。</p>
        <dl className="acc-stats">
          <div className="acc-stat"><dt>累積消費</dt><dd>{totalSpent.toLocaleString()}<small>NT$</small></dd></div>
          <div className="acc-stat"><dt>MICHI POINT</dt><dd>{points.toLocaleString()}<small>P</small></dd></div>
          <div className="acc-stat"><dt>訂單數</dt><dd>{orderCount}<small>筆</small></dd></div>
        </dl>
      </div>

      {/* 代打進度 strip（有登記紀錄才顯示） */}
      {latest && (
        <div className="boost-strip" onClick={() => goTab(MENU_ACTIONS.BOOST)}>
          <div className="ic"><Icon name="bolt" size={24} /></div>
          <div className="tx">
            <b>代打登記 {latest.refNo || ''} · {latest.productName}</b>
            <small>目前狀態：{latest.statusLabel}　·　{(latest.items?.length ?? 0)} 張卡　·　NT${Math.max(0, (latest.estimatedTotal ?? 0) - (latest.pointsRedeemed ?? 0)).toLocaleString()}</small>
          </div>
          <span className="go">查看代打資料 <Icon name="arrow" size={15} /></span>
        </div>
      )}

      {/* 等級進度：功能未做，暫時隱藏 */}

      {/* 最近訂單 */}
      <div className="card">
        <div className="card-head"><h3>最近訂單</h3><span className="more" onClick={() => goTab(MENU_ACTIONS.ORDERS)}>查看全部 →</span></div>
        {ordersLoading ? <div className="empty">載入中…</div> : <OrdersTable orders={orders.slice(0, 3)} />}
      </div>
    </>
  );
}

/* ---------------- 訂單 ---------------- */
function OrdersScreen({ orders, ordersLoading }) {
  const [f, setF] = useState('all');
  // 我的訂單只列實體商品（虛擬 / 代打在「代打資料」分頁）；僅保留狀態篩選
  const FILTERS = [['all', '全部'], ['processing', '處理中']];
  const list = orders.filter(o => f === 'all' ? true : o.status === 'processing');
  return (
    <>
      <div className="acc-pagehead"><h2>我的訂單</h2><span className="sub">共 {orders.length} 筆</span></div>
      <div className="card">
        <div className="filter-row">
          {FILTERS.map(([v, l]) => <button key={v} type="button" className={f === v ? 'on' : ''} onClick={() => setF(v)}>{l}</button>)}
        </div>
        {ordersLoading
          ? <div className="empty">載入中…</div>
          : list.length ? <OrdersTable orders={list} /> : <div className="empty">此分類沒有訂單。</div>}
      </div>
    </>
  );
}

/* ---------------- 代打資料 ---------------- */
function BoostScreen({ navigate, inquiries, inquiriesLoading, inquiriesError, refetchInquiries }) {
  const { showToast } = useToast();
  const [tab, setTab] = useState('active'); // active = 進行中（非已結案）| closed = 已結案
  const [confirmingId, setConfirmingId] = useState(null);

  const active = inquiries.filter(r => r.status !== 'CLOSED');
  const closed = inquiries.filter(r => r.status === 'CLOSED');
  const shown = tab === 'closed' ? closed : active;

  const handleConfirm = async (r) => {
    setConfirmingId(r.id);
    try {
      await confirmInquiry(r.id);
      showToast('已確認結案，感謝！', 'success');
      refetchInquiries();
    } catch (err) {
      showToast(`結案失敗：${err.message}`, 'error');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <>
      <div className="acc-pagehead"><h2>代打資料</h2><span className="sub">登記紀錄與常用卡片</span></div>

      <div className="card">
        <div className="card-head">
          <h3>登記紀錄 · 狀態追蹤</h3>
          <span className="more" onClick={() => navigate('/virtual')}>+ 新增登記</span>
        </div>
        {/* 進行中 / 已結案 切換 */}
        <div className="filter-row" style={{ marginBottom: 12 }}>
          <button type="button" className={tab === 'active' ? 'on' : ''} onClick={() => setTab('active')}>進行中（{active.length}）</button>
          <button type="button" className={tab === 'closed' ? 'on' : ''} onClick={() => setTab('closed')}>已結案（{closed.length}）</button>
        </div>
        {inquiriesLoading
          ? <div className="empty">載入中…</div>
          : inquiriesError
            ? <div className="empty">登記紀錄載入失敗。<button type="button" className="link-inline" onClick={refetchInquiries} style={{ background: 'none', border: 'none', color: 'var(--navy)', fontWeight: 700, cursor: 'pointer' }}>重試</button></div>
            : inquiries.length === 0
              ? <div className="empty">還沒有登記紀錄。<Link to="/virtual" style={{ color: 'var(--navy)', fontWeight: 700 }}>去逛逛虛擬商品 →</Link></div>
              : shown.length === 0
                ? <div className="empty">{tab === 'closed' ? '還沒有已結案的登記。' : '沒有進行中的登記。'}</div>
                : shown.map(r => <RegRecord key={r.id} r={r} onConfirm={handleConfirm} confirming={confirmingId === r.id} />)}
      </div>

      <SavedCardsSection />
    </>
  );
}

/* ---------------- 常用代打卡片（CRUD）---------------- */
function SavedCardsSection() {
  const { showToast } = useToast();
  const [cards, setCards] = useState(null); // null = 載入中
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(null); // null=無 | 'new' | card物件
  const [saving, setSaving] = useState(false);

  const load = () => {
    setError(false);
    getSavedCards().then(setCards).catch(() => { setError(true); setCards([]); });
  };
  useEffect(load, []);

  const handleSave = async (form) => {
    if (!form.cardName.trim()) { showToast('請填寫卡名', 'error'); return; }
    setSaving(true);
    try {
      const payload = { cardName: form.cardName.trim(), account: form.account.trim(), note: form.note.trim() };
      if (editing === 'new') {
        await createSavedCard(payload);
        showToast('已新增常用卡片', 'success');
      } else {
        await updateSavedCard(editing.id, payload);
        showToast('已更新卡片', 'success');
      }
      setEditing(null);
      load();
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (card) => {
    if (!window.confirm(`確定刪除「${card.cardName}」？`)) return;
    try {
      await deleteSavedCard(card.id);
      showToast('已刪除', 'info');
      load();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error');
    }
  };

  const handleSetDefault = async (card) => {
    try {
      await setDefaultCard(card.id);
      showToast(`已將「${card.cardName}」設為預設`, 'success');
      load();
    } catch (err) {
      showToast(`設定失敗：${err.message}`, 'error');
    }
  };

  return (
    <div className="card">
      <div className="card-head"><h3>常用代打卡片</h3><span className="sub">登記時可一鍵帶入</span></div>

      {editing && (
        <SavedCardForm
          initial={editing === 'new' ? null : editing}
          saving={saving}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      )}

      {cards === null
        ? <div className="empty">載入中…</div>
        : error
          ? <div className="empty">卡片載入失敗。<button type="button" onClick={load} style={{ background: 'none', border: 'none', color: 'var(--navy)', fontWeight: 700, cursor: 'pointer' }}>重試</button></div>
          : (
            <div className="sc-grid">
              {cards.map(sc => (
                <div className="sc" key={sc.id}>
                  <div className="sc-top">
                    <span className="sc-label">{sc.cardName}</span>
                    {sc.isDefault && (
                      <span style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 999, background: 'var(--navy)', color: '#fff', font: '700 10px "Noto Sans TC", sans-serif' }}>預設</span>
                    )}
                    {sc.note && <span className="sc-note">{sc.note}</span>}
                    <div className="sc-actions">
                      <button type="button" aria-label="編輯" onClick={() => setEditing(sc)}><Icon name="edit" size={15} /></button>
                      <button type="button" aria-label="刪除" onClick={() => handleDelete(sc)}><Icon name="trash" size={15} /></button>
                    </div>
                  </div>
                  <div className="sc-row"><span className="k">帳號</span><span className="v">{sc.account || '—'}</span></div>
                  {!sc.isDefault && (
                    <button type="button" onClick={() => handleSetDefault(sc)}
                      style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, font: '700 12px "Noto Sans TC", sans-serif', color: 'var(--navy)', cursor: 'pointer' }}>
                      設為預設
                    </button>
                  )}
                </div>
              ))}
              {!editing && (
                <button type="button" className="addr-add" style={{ minHeight: 110 }} onClick={() => setEditing('new')}>
                  <Icon name="plus" size={18} /> 新增常用卡片
                </button>
              )}
            </div>
          )}
    </div>
  );
}

function SavedCardForm({ initial, saving, onSave, onCancel }) {
  const [form, setForm] = useState({
    cardName: initial?.cardName || '', account: initial?.account || '', note: initial?.note || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="co-new-addr" style={{ marginBottom: 16 }}>
      <div className="co-row2">
        <div className="co-field"><label>卡名 *</label><input placeholder="例：我的主卡" value={form.cardName} onChange={e => set('cardName', e.target.value)} /></div>
        <div className="co-field"><label>機台帳號（信箱）</label><input placeholder="player@example.com" value={form.account} onChange={e => set('account', e.target.value)} /></div>
      </div>
      <div className="co-field"><label>備註</label><input placeholder="選填" value={form.note} onChange={e => set('note', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => onSave(form)}
          style={{ padding: '9px 20px', borderRadius: 999, border: 'none', background: 'var(--navy)', color: '#fff', font: '800 13px "Noto Sans TC", sans-serif', cursor: 'pointer' }}>
          {saving ? '儲存中…' : (initial ? '儲存變更' : '新增')}
        </button>
        <button type="button" disabled={saving} onClick={onCancel}
          style={{ padding: '9px 20px', borderRadius: 999, border: '1.5px solid var(--line)', background: '#fff', font: '700 13px "Noto Sans TC", sans-serif', cursor: 'pointer' }}>
          取消
        </button>
      </div>
    </div>
  );
}

/* ---------------- 點數 ---------------- */
function PointsScreen({ points, data }) {
  const txns = data?.transactions ?? [];
  // 到期日（最後活動後 6 個月）；有餘額才顯示
  const expireText = (points > 0 && data?.expireAt)
    ? fmtDateTaipei(data.expireAt)
    : null;
  return (
    <>
      <div className="acc-pagehead"><h2>點數紀錄</h2><span className="sub">MICHI POINT</span></div>
      <div className="pt-hero">
        <div className="ic"><Icon name="coins" size={28} /></div>
        <div>
          <div className="lb">可用點數</div>
          <div className="big">{points.toLocaleString()}<small> P</small></div>
        </div>
        {expireText && (
          <div className="exp">將於<br /><span style={{ opacity: .7 }}>{expireText} 到期</span></div>
        )}
      </div>
      <div className="card">
        <div className="card-head"><h3>異動明細</h3></div>
        {txns.length === 0 ? (
          <div className="empty">還沒有點數紀錄。消費滿額、完成訂單即可累積。</div>
        ) : (
          <table className="pledger">
            <tbody>
              {txns.map((p, i) => (
                <tr key={i}>
                  <td className="dt">{p.date}</td>
                  <td className="lb">{p.label}</td>
                  <td className={`delta ${p.type}`}>{p.delta > 0 ? '+' : ''}{p.delta} P</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ---------------- 地址（真實 CRUD）---------------- */
function AddressForm({ initial, saving, onSave, onCancel }) {
  const [form, setForm] = useState({
    recipientName: initial?.recipientName || '', phone: initial?.phone || '',
    address: initial?.address || '', label: initial?.label || '', isDefault: initial?.isDefault || false,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="co-new-addr" style={{ marginBottom: 16 }}>
      <div className="co-row2">
        <div className="co-field"><label>姓名 *</label><input value={form.recipientName} onChange={e => set('recipientName', e.target.value)} placeholder="收件人姓名" /></div>
        <div className="co-field"><label>電話 *</label><input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="09xx-xxx-xxx" /></div>
      </div>
      <div className="co-field"><label>地址 *</label><input value={form.address} onChange={e => set('address', e.target.value)} placeholder="縣市 · 鄉鎮 · 詳細地址" /></div>
      <div className="co-row2">
        <div className="co-field"><label>標籤</label><input value={form.label} onChange={e => set('label', e.target.value)} placeholder="住家 / 公司（選填）" /></div>
        <div className="co-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.isDefault} onChange={e => set('isDefault', e.target.checked)} /> 設為預設地址
          </label>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => onSave(form)}
          style={{ padding: '9px 20px', borderRadius: 999, border: 'none', background: 'var(--navy)', color: '#fff', font: '800 13px "Noto Sans TC", sans-serif', cursor: 'pointer' }}>
          {saving ? '儲存中…' : (initial ? '儲存變更' : '新增')}
        </button>
        <button type="button" disabled={saving} onClick={onCancel}
          style={{ padding: '9px 20px', borderRadius: 999, border: '1.5px solid var(--line)', background: '#fff', font: '700 13px "Noto Sans TC", sans-serif', cursor: 'pointer' }}>取消</button>
      </div>
    </div>
  );
}

function AddressesScreen() {
  const { showToast } = useToast();
  const [addrs, setAddrs] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(null); // 'new' | addr
  const [saving, setSaving] = useState(false);

  const load = () => {
    setError(false);
    getAddresses().then(setAddrs).catch(() => { setError(true); setAddrs([]); });
  };
  useEffect(load, []);

  const handleSave = async (form) => {
    if (!form.recipientName.trim() || !form.phone.trim() || !form.address.trim()) {
      showToast('請填寫姓名、電話與地址', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = { recipientName: form.recipientName.trim(), phone: form.phone.trim(), address: form.address.trim(), label: form.label.trim(), isDefault: form.isDefault };
      if (editing === 'new') { await createAddress(payload); showToast('已新增地址', 'success'); }
      else { await updateAddress(editing.id, payload); showToast('已更新地址', 'success'); }
      setEditing(null);
      load();
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    } finally { setSaving(false); }
  };

  const handleDefault = async (a) => {
    try { await setDefaultAddress(a.id); load(); } catch (err) { showToast(`失敗：${err.message}`, 'error'); }
  };
  const handleDelete = async (a) => {
    if (!window.confirm(`確定刪除「${a.recipientName} · ${a.address}」？`)) return;
    try { await deleteAddress(a.id); showToast('已刪除', 'info'); load(); } catch (err) { showToast(`刪除失敗：${err.message}`, 'error'); }
  };

  return (
    <>
      <div className="acc-pagehead"><h2>收貨地址</h2><span className="sub">{addrs?.length ?? 0} 筆</span></div>
      <div className="card">
        {editing && <AddressForm initial={editing === 'new' ? null : editing} saving={saving} onSave={handleSave} onCancel={() => setEditing(null)} />}

        {addrs === null
          ? <div className="empty">載入中…</div>
          : error
            ? <div className="empty">地址載入失敗。<button type="button" onClick={load} style={{ background: 'none', border: 'none', color: 'var(--navy)', fontWeight: 700, cursor: 'pointer' }}>重試</button></div>
            : addrs.length === 0 && !editing
              ? <div className="empty">還沒有收貨地址。</div>
              : addrs.map(a => (
                <div className={`addr ${a.isDefault ? 'default' : ''}`} key={a.id}>
                  <div className="addr-top">
                    <span className="addr-name">{a.recipientName}</span>
                    <span className="addr-phone">{a.phone}</span>
                    {a.isDefault && <span className="addr-default-tag">預設</span>}
                    {a.label && <span className="addr-label-tag">{a.label}</span>}
                  </div>
                  <div className="addr-line">{a.address}</div>
                  <div className="addr-actions">
                    <button type="button" onClick={() => setEditing(a)}><Icon name="edit" size={13} /> 編輯</button>
                    {!a.isDefault && <button type="button" onClick={() => handleDefault(a)}>設為預設</button>}
                    <button type="button" onClick={() => handleDelete(a)}><Icon name="trash" size={13} /> 刪除</button>
                  </div>
                </div>
              ))}
        {!editing && addrs !== null && !error && (
          <button type="button" className="addr-add" onClick={() => setEditing('new')}><Icon name="plus" size={18} /> 新增地址</button>
        )}
      </div>
    </>
  );
}

/* ---------------- 個人資料（可編輯姓名 + 改密碼）---------------- */
function ProfileScreen({ user, safeName, initial }) {
  const { setUser } = useAuth();
  const { showToast } = useToast();
  const [name, setName] = useState(safeName);
  const [savingName, setSavingName] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [savingPw, setSavingPw] = useState(false);

  const saveName = async () => {
    if (!name.trim()) { showToast('請填寫姓名', 'error'); return; }
    if (name.trim() === safeName) { showToast('名稱沒有變更', 'info'); return; }
    setSavingName(true);
    try {
      const updated = await updateProfile({ name: name.trim() });
      setUser(u => ({ ...u, name: updated.name, initial: updated.initial ?? u.initial }));
      showToast('已更新個人資料', 'success');
    } catch (err) {
      showToast(`更新失敗：${err.message}`, 'error');
    } finally {
      setSavingName(false);
    }
  };

  const savePw = async () => {
    if (!pw.current || !pw.next) { showToast('請填寫目前密碼與新密碼', 'error'); return; }
    if (pw.next.length < 8) { showToast('新密碼至少 8 碼', 'error'); return; }
    if (pw.next !== pw.confirm) { showToast('兩次新密碼不一致', 'error'); return; }
    setSavingPw(true);
    try {
      await changePassword({ currentPassword: pw.current, newPassword: pw.next });
      setPw({ current: '', next: '', confirm: '' });
      showToast('密碼已更新', 'success');
    } catch (err) {
      showToast(`修改失敗：${err.message}`, 'error');
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <>
      <div className="acc-pagehead"><h2>個人資料</h2></div>
      <div className="card">
        <div className="avatar-row">
          <div className="avatar-lg">{initial}</div>
          <div><div className="avatar-hint">會員資料 · email 為登入帳號，不可變更</div></div>
        </div>
        <div className="form-grid">
          <div className="field"><label>姓名</label><input value={name} onChange={e => setName(e.target.value)} /></div>
          <div className="field"><label>電子信箱（不可改）</label><input value={user.email} disabled style={{ background: 'var(--ice, #f4f7ff)', color: 'var(--ink-soft)' }} /></div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={savingName} onClick={saveName}>
            <Icon name="check" size={17} /> {savingName ? '儲存中…' : '儲存姓名'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><h3>修改密碼</h3></div>
        <div className="form-grid">
          <div className="field"><label>目前密碼</label><input type="password" value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} /></div>
          <div className="field" />
          <div className="field"><label>新密碼（至少 8 碼）</label><input type="password" value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} /></div>
          <div className="field"><label>確認新密碼</label><input type="password" value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} /></div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={savingPw} onClick={savePw}>
            <Icon name="check" size={17} /> {savingPw ? '更新中…' : '更新密碼'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ---------------- 通知設定 ---------------- */
function NotifsScreen() {
  const [state, setState] = useState(() => Object.fromEntries(NOTIFS.map(n => [n.id, n.on])));
  return (
    <>
      <div className="acc-pagehead"><h2>通知設定</h2><span className="sub">選擇你想收到的通知</span></div>
      <div className="card">
        {NOTIFS.map(n => (
          <div className="notif" key={n.id}>
            <div className="notif-info"><b>{n.label}</b><small>{n.desc}</small></div>
            <div
              className={`toggle ${state[n.id] ? 'on' : ''}`}
              onClick={() => setState(s => ({ ...s, [n.id]: !s[n.id] }))}
              role="switch"
              aria-checked={state[n.id]}
              aria-label={n.label}
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setState(s => ({ ...s, [n.id]: !s[n.id] })); } }}
            />
          </div>
        ))}
        <div className="form-actions"><button type="button" className="btn btn-primary"><Icon name="check" size={17} /> 儲存設定</button></div>
      </div>
    </>
  );
}

export default AccountPage;
